/**
 * TanStack Query hooks over the Eden Treaty client. Centralizes query keys and
 * cache invalidation so pages stay declarative.
 *
 * Every log mutation is optimistic: the cache changes before the request is
 * sent, the sheet closes at once, and the server row replaces the temporary
 * one on success (or the cache rolls back on error). That is what makes a
 * 3am tap feel instant even on a flaky connection.
 */
import {
  type BabySeries,
  type BottleUnit,
  type Sex,
  type TrackableType,
  type Tracked,
  tracked,
  type WeightUnit,
} from "@babia/checks";
import {
  type InfiniteData,
  mutationOptions,
  type QueryClient,
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { api, unwrap } from "../api.ts";
import { type EntryPatch, holdsPatch, withoutPatch, withPatch } from "./entry-edit.ts";
import type { EntryLike } from "./entry-text.ts";
import type { StoredWeight } from "./growth-chart.ts";
import {
  deviceSubscription,
  hasKey,
  notificationPermission,
  turnOffPush,
  turnOnPush,
} from "./push.ts";
import { HISTORY_DAYS } from "./overview.ts";
import { CHANGE_WINDOW_MS, type EntryChange } from "./revisions.ts";
import { addLocalDays, calendarDate, startOfLocalDay, toDate } from "./time.ts";
import { sharedSyncOptions } from "./sync.ts";
import { type Cursor, type EntryPage, LIVE_WINDOW_MS, PAGE_SIZE, toPage } from "./timeline.ts";

declare module "@tanstack/react-query" {
  interface Register {
    /** Names the write in the "api write failed" analytics event (main.tsx). */
    mutationMeta: { action: string };
  }
}

export const keys = {
  health: ["health"] as const,
  babies: ["babies"] as const,
  entries: ["entries"] as const,
  // Under "entries" so a logged, ended, or removed entry invalidates them too.
  entryHistory: ["entries", "history"] as const,
  // A baby's older entries, a page at a time: [...entryPages, babyId].
  entryPages: ["entries", "pages"] as const,
  milestones: ["entries", "milestones"] as const,
  // The weeks and months of life: [...series, since].
  series: ["entries", "series"] as const,
  // The growth chart's weights.
  weights: ["entries", "weights"] as const,
  // An assistant's edits and deletes that can still be put back.
  entryChanges: ["entries", "changes"] as const,
  household: ["household"] as const,
  assistants: ["assistants"] as const,
  push: ["push"] as const,
  pushDevice: ["push", "device"] as const,
};

export interface Entry extends EntryLike {
  createdByUserId: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export const isPending = (e: { id: string }): boolean => e.id.startsWith("tmp-");

type RawEntry = Awaited<ReturnType<typeof fetchEntriesRaw>>[number];

/** The timestamps arrive as ISO strings (api.ts `Wire`); this makes them `Date`s. */
function hydrateEntry(raw: RawEntry): Entry {
  return {
    ...raw,
    at: toDate(raw.at),
    endedAt: raw.endedAt ? toDate(raw.endedAt) : null,
    createdAt: toDate(raw.createdAt),
    updatedAt: toDate(raw.updatedAt),
  };
}

const fetchEntriesRaw = (signal: AbortSignal, since = new Date(Date.now() - LIVE_WINDOW_MS)) =>
  unwrap(api.entries.get({ query: { since: since.toISOString() }, fetch: { signal } }));

// --- Babies ----------------------------------------------------------------

export type Baby = Awaited<ReturnType<typeof fetchBabiesRaw>>[number];
const fetchBabiesRaw = (signal: AbortSignal) => unwrap(api.babies.get({ fetch: { signal } }));

/**
 * Every baby response passes through here. The birth and due dates arrive as
 * the YYYY-MM-DD strings sent (api.ts turns Eden's date parsing off), and
 * `calendarDate` keeps them so if a `Date` ever comes back.
 */
const hydrateBaby = (raw: Baby): Baby => ({
  ...raw,
  birthDate: calendarDate(raw.birthDate),
  dueDate: calendarDate(raw.dueDate),
});

const fetchBabies = async ({ signal }: { signal: AbortSignal }) =>
  (await fetchBabiesRaw(signal)).map(hydrateBaby);

export function useBabies() {
  const pendingWrites = useIsMutating({ mutationKey: keys.babies });
  return useQuery({
    queryKey: keys.babies,
    queryFn: fetchBabies,
    ...sharedSyncOptions(pendingWrites),
  });
}

/** A baby to add: a name, and a birth date (YYYY-MM-DD) if the parent gave one. */
export interface NewBaby {
  name: string;
  birthDate?: string;
}

export function useCreateBabies() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: keys.babies,
    meta: { action: "babies add" },
    mutationFn: async (babies: NewBaby[]) =>
      (await unwrap(api.babies.post({ babies }))).map(hydrateBaby),
    onMutate: () => qc.cancelQueries({ queryKey: keys.babies }),
    onSuccess: (list) => qc.setQueryData(keys.babies, list),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.babies }),
  });
}

export function useUpdateBaby() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: keys.babies,
    meta: { action: "baby update" },
    mutationFn: async ({
      id,
      ...patch
    }: {
      id: string;
      name?: string;
      aliases?: string[];
      /** YYYY-MM-DD; null clears it. */
      birthDate?: string | null;
      /** YYYY-MM-DD; null clears it. */
      dueDate?: string | null;
      /** Only for the WHO growth curves; null clears it. */
      sex?: Sex | null;
    }) => hydrateBaby(await unwrap(api.babies({ id }).patch(patch))),
    onMutate: () => qc.cancelQueries({ queryKey: keys.babies }),
    onSuccess: (updated) =>
      qc.setQueryData<Baby[]>(keys.babies, (old = []) =>
        old.map((baby) => (baby.id === updated.id ? updated : baby)),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.babies }),
  });
}

export function useDeleteBaby() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: keys.babies,
    meta: { action: "baby remove" },
    mutationFn: (id: string) => unwrap(api.babies({ id }).delete()),
    onMutate: () => qc.cancelQueries({ queryKey: keys.babies }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.babies });
      void qc.invalidateQueries({ queryKey: keys.entries });
    },
  });
}

export function useReorderBabies() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: keys.babies,
    meta: { action: "babies reorder" },
    mutationFn: (ids: string[]) => unwrap(api.babies.order.put({ ids })),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: keys.babies });
      const previous = qc.getQueryData<Baby[]>(keys.babies);
      if (previous) {
        const byId = new Map(previous.map((b) => [b.id, b]));
        qc.setQueryData<Baby[]>(
          keys.babies,
          ids.map((id, i) => ({ ...byId.get(id)!, sortOrder: i })),
        );
      }
      return { previous };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.babies, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.babies }),
  });
}

// --- Entries ---------------------------------------------------------------

export function useEntries() {
  const pendingWrites = useIsMutating({ mutationKey: keys.entries });
  return useQuery({
    queryKey: keys.entries,
    queryFn: async ({ signal }) => (await fetchEntriesRaw(signal)).map(hydrateEntry),
    ...sharedSyncOptions(pendingWrites),
  });
}

/**
 * The overview's two weeks. Old entries rarely change, so this refreshes on a
 * slow beat; the overview lays the 5s live list over its recent end
 * (`mergeHistory`), which is what keeps the last two days current.
 */
export function useEntryHistory() {
  const pendingWrites = useIsMutating({ mutationKey: keys.entries });
  return useQuery({
    queryKey: keys.entryHistory,
    queryFn: async ({ signal }) =>
      (await fetchEntriesRaw(signal, addLocalDays(new Date(), -HISTORY_DAYS))).map(hydrateEntry),
    ...slowSyncOptions(pendingWrites),
  });
}

/**
 * One baby's older entries for the log, a page at a time, once `open` (the
 * parent asked to see them). The first page starts at today's local midnight,
 * worked out again on every refresh, so the pages always reach the live
 * list's window as it moves (see `olderEntries`). Refreshes on the overview's
 * slow beat, and after every write like the live list.
 */
export function useEntryPages(babyId: string, open: boolean) {
  const pendingWrites = useIsMutating({ mutationKey: keys.entries });
  const sync = slowSyncOptions(pendingWrites);
  return useInfiniteQuery({
    queryKey: [...keys.entryPages, babyId],
    initialPageParam: null as Cursor | null,
    queryFn: async ({ pageParam, signal }): Promise<EntryPage<Entry>> => {
      const before = pageParam?.at ?? startOfLocalDay(new Date());
      const rows = await unwrap(
        api.entries.history.get({
          query: {
            babyId,
            before: before.toISOString(),
            ...(pageParam ? { beforeId: pageParam.id } : {}),
            limit: PAGE_SIZE,
          },
          fetch: { signal },
        }),
      );
      return toPage(rows.map(hydrateEntry));
    },
    getNextPageParam: (page) => page.next,
    ...sync,
    enabled: sync.enabled && open,
  });
}

/** The weeks and months' read: every baby's bottles, changes, and sleeps, and when logging began. */
export interface EntrySeries {
  began: Date | null;
  babies: BabySeries[];
}

/**
 * Up to a year of bottles, changes, and sleeps for the overview's weeks and
 * months of life, once `open` (a parent chose Weeks or Months): times,
 * ounces, and kinds only, so the phone decides each entry's local day.
 * `since` (from `seriesSince`) holds still all day, so the key moves only at
 * midnight or with a birth date; null fetches nothing. Refreshes on the slow
 * beat with no live list laid over it: the charts never count today, and a
 * write refreshes it.
 */
export function useEntrySeries(since: string | null, open: boolean) {
  const pendingWrites = useIsMutating({ mutationKey: keys.entries });
  const sync = slowSyncOptions(pendingWrites);
  return useQuery({
    queryKey: [...keys.series, since],
    queryFn: async ({ signal }): Promise<EntrySeries> => {
      const raw = await unwrap(
        api.entries.series.get({ query: { since: since ?? "" }, fetch: { signal } }),
      );
      return { began: raw.began ? toDate(raw.began) : null, babies: raw.babies };
    },
    // A read from the same or an earlier `since` holds every entry this one
    // needs, so it stays on screen while this loads. One from a later `since`
    // (a birth date moved back) lacks the older months: show loading instead.
    placeholderData: (previous, previousQuery) => {
      const from = previousQuery?.queryKey[2];
      return typeof from === "string" && since !== null && from <= since ? previous : undefined;
    },
    ...sync,
    enabled: sync.enabled && since !== null && open,
  });
}

/**
 * Every weight with a number, oldest first, for the overview's growth chart.
 * Refreshes on the slow beat and after every write; the overview lays the
 * live list over it (`weightPoints`), so a weight another parent logs shows
 * at once.
 */
export function useWeights() {
  const pendingWrites = useIsMutating({ mutationKey: keys.entries });
  return useQuery({
    queryKey: keys.weights,
    queryFn: async ({ signal }): Promise<StoredWeight[]> =>
      (await unwrap(api.entries.weights.get({ fetch: { signal } }))).map((w) => ({
        ...w,
        at: new Date(w.at),
      })),
    ...slowSyncOptions(pendingWrites),
  });
}

/** Slow-moving overview data: on open, on return to the app, and every 5 min. */
function slowSyncOptions(pendingWrites: number) {
  return {
    enabled: pendingWrites === 0,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  } as const;
}

export type BabyMilestones = Awaited<ReturnType<typeof fetchMilestones>>[number];
const fetchMilestones = async ({ signal }: { signal: AbortSignal }) =>
  (await unwrap(api.milestones.get({ fetch: { signal } }))).map((m) => ({
    ...m,
    trackingSince: m.trackingSince ? toDate(m.trackingSince) : null,
    longestSleep: m.longestSleep
      ? { at: toDate(m.longestSleep.at), endedAt: toDate(m.longestSleep.endedAt) }
      : null,
    biggestBottle: m.biggestBottle ? { ...m.biggestBottle, at: toDate(m.biggestBottle.at) } : null,
    longestBottleGap: m.longestBottleGap
      ? { at: toDate(m.longestBottleGap.at), endedAt: toDate(m.longestBottleGap.endedAt) }
      : null,
    longestSleepTogether: m.longestSleepTogether
      ? {
          ...m.longestSleepTogether,
          at: toDate(m.longestSleepTogether.at),
          endedAt: toDate(m.longestSleepTogether.endedAt),
        }
      : null,
    firsts: m.firsts.map((f) => ({
      ...f,
      at: toDate(f.at),
      endedAt: f.endedAt ? toDate(f.endedAt) : null,
    })),
  }));

/**
 * Each baby's all-time milestones and records (server-side: they need every
 * entry). The server leaves out the types the household turned off and sizes
 * bottles in the household's unit, so the key carries both: any phone that
 * sees either change, a partner's too, fetches again at once. A tracking
 * change keeps the old list on screen meanwhile; a unit change does not,
 * since its sizes are in the old unit and would read as the new one.
 */
export function useMilestones() {
  const pendingWrites = useIsMutating({ mutationKey: keys.entries });
  const tracking = useTracking();
  const unit = useBottleUnit();
  const sync = slowSyncOptions(pendingWrites);
  return useQuery({
    queryKey: [...keys.milestones, tracking.untracked, unit],
    queryFn: fetchMilestones,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey.at(-1) === unit ? previous : undefined,
    ...sync,
    // Wait for the household, so a cold start fetches once, for the right list.
    enabled: sync.enabled && tracking.ready,
  });
}

export type EntryCreateBody = Parameters<typeof api.entries.post>[0];
/** A new entry, with the temporary id its row shows under when the caller picks it. */
export type NewEntry = EntryCreateBody & { tempId?: string };

/** The id of a row the server hasn't saved yet (`isPending`). */
export const newTempId = (): string => `tmp-${crypto.randomUUID()}`;

/**
 * A save for every baby (#89): one bottle each, in tab order, with one time,
 * one amount, and the household's feed label. `together` tells the server's
 * analytics that one tap logged them all.
 */
export function bottlesForAll(
  babyIds: readonly string[],
  at: Date,
  amountOz: number,
  label: string,
): (EntryCreateBody & { at: Date })[] {
  return babyIds.map((babyId) => ({
    babyId,
    type: "feed",
    at,
    amountOz,
    label,
    together: babyIds.length,
  }));
}

/** The row a new entry shows as until the server's row replaces it. */
export function optimisticEntry(body: EntryCreateBody, id: string, now: Date): Entry {
  return {
    id,
    babyId: body.babyId,
    type: body.type,
    at: body.at ? toDate(body.at) : now,
    endedAt: null,
    amountOz: body.amountOz ?? null,
    diaperKind: body.diaperKind ?? null,
    label: body.label ?? null,
    note: body.note ?? null,
    noteKind: body.type === "note" ? (body.noteKind ?? "general") : null,
    doseAmount: body.type === "meds" ? (body.doseAmount ?? null) : null,
    doseUnit: body.type === "meds" ? (body.doseUnit ?? null) : null,
    weightGrams:
      body.type === "note" && body.noteKind === "weight" ? (body.weightGrams ?? null) : null,
    createdByUserId: null,
    createdAt: now,
    updatedAt: now,
  };
}

export function useCreateEntry() {
  const qc = useQueryClient();
  return useMutation({
    // Commit a dismissed removal before a following action (especially a new nap).
    scope: { id: "entry-writes" },
    mutationKey: keys.entries,
    meta: { action: "entry create" },
    // The temporary id stays on the phone: the body refuses unknown fields.
    mutationFn: async ({ tempId: _tempId, ...body }: NewEntry) =>
      hydrateEntry(await unwrap(api.entries.post(body))),
    onMutate: async ({ tempId: picked, ...body }) => {
      await qc.cancelQueries({ queryKey: keys.entries });
      const tempId = picked ?? newTempId();
      const optimistic = optimisticEntry(body, tempId, new Date());
      qc.setQueryData<Entry[]>(keys.entries, (old = []) => [optimistic, ...old]);
      return { tempId };
    },
    onSuccess: (created, _body, ctx) => {
      qc.setQueryData<Entry[]>(keys.entries, (old = []) =>
        old.map((e) => (e.id === ctx.tempId ? created : e)),
      );
    },
    onError: (_err, _body, ctx) => {
      if (ctx) {
        qc.setQueryData<Entry[]>(keys.entries, (old = []) =>
          old.filter((e) => e.id !== ctx.tempId),
        );
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.entries }),
  });
}

export function useEndSleep() {
  const qc = useQueryClient();
  return useMutation({
    // Commit a dismissed removal before a following action (especially a new nap).
    scope: { id: "entry-writes" },
    mutationKey: keys.entries,
    meta: { action: "nap end" },
    mutationFn: async ({ id, endedAt }: { id: string; endedAt: Date }) =>
      hydrateEntry(await unwrap(api.entries({ id }).patch({ endedAt }))),
    onMutate: async ({ id, endedAt }) => {
      await qc.cancelQueries({ queryKey: keys.entries });
      const previous = qc.getQueryData<Entry[]>(keys.entries)?.find((e) => e.id === id);
      qc.setQueryData<Entry[]>(keys.entries, (old = []) =>
        old.map((e) => (e.id === id ? { ...e, endedAt } : e)),
      );
      return { previous };
    },
    onError: (_err, { id, endedAt }, ctx) => {
      if (ctx?.previous) {
        const previous = ctx.previous;
        qc.setQueryData<Entry[]>(keys.entries, (old = []) =>
          old.map((e) =>
            e.id === id && e.endedAt?.getTime() === endedAt.getTime()
              ? { ...e, endedAt: previous.endedAt }
              : e,
          ),
        );
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.entries }),
  });
}

export function useDeleteEntry() {
  const qc = useQueryClient();
  return useMutation({
    // Commit a dismissed removal before a following action (especially a new nap).
    scope: { id: "entry-writes" },
    mutationKey: keys.entries,
    meta: { action: "entry delete" },
    mutationFn: (id: string) => unwrap(api.entries({ id }).delete()),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: keys.entries });
      const previous = qc.getQueryData<Entry[]>(keys.entries)?.find((e) => e.id === id);
      qc.setQueryData<Entry[]>(keys.entries, (old = []) => old.filter((e) => e.id !== id));
      return { removed: previous };
    },
    // An older row lives only in its page. Drop it there before the log
    // reveals what it hid, so the row never comes back until the refresh.
    onSuccess: (_removed, id) => mapPages(qc, (entries) => entries.filter((e) => e.id !== id)),
    onError: (_err, _id, ctx) => {
      if (ctx?.removed) {
        const previous = ctx.removed;
        qc.setQueryData<Entry[]>(keys.entries, (old = []) =>
          old.some((e) => e.id === previous.id) ? old : [...old, previous],
        );
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.entries }),
  });
}

/** Change the rows of every baby's loaded older pages. */
function mapPages(qc: QueryClient, change: (entries: Entry[]) => Entry[]) {
  qc.setQueriesData<InfiniteData<EntryPage<Entry>>>({ queryKey: keys.entryPages }, (old) =>
    old
      ? { ...old, pages: old.pages.map((page) => ({ ...page, entries: change(page.entries) })) }
      : old,
  );
}

/**
 * Edits on their way, per cache and by entry id, with the row as the server
 * last had it. An earlier reply must not hide a newer edit (an Undo), and a
 * failed edit goes back to that row, never to another edit's unsaved values.
 */
type EditsInFlight = Map<string, { count: number; confirmed: Entry }>;
const inFlightByCache = new WeakMap<QueryClient, EditsInFlight>();
function inFlightFor(qc: QueryClient): EditsInFlight {
  let edits = inFlightByCache.get(qc);
  if (!edits) inFlightByCache.set(qc, (edits = new Map()));
  return edits;
}

/** Sends one edit and returns the server's row; a test passes its own. */
export type SendEdit = (id: string, patch: EntryPatch) => Promise<Entry>;

const sendEdit: SendEdit = async (id, patch) =>
  hydrateEntry(await unwrap(api.entries({ id }).edit.patch(patch)));

/**
 * A parent's fix from the edit sheet (#91): only the changed fields. The row
 * changes at once, in the live list or in the older page it lives in.
 */
export function useUpdateEntry() {
  const qc = useQueryClient();
  return useMutation(updateEntryOptions(qc));
}

/** `useUpdateEntry`'s options, apart from the hook so a test can run them. */
export function updateEntryOptions(qc: QueryClient, send: SendEdit = sendEdit) {
  const editsInFlight = inFlightFor(qc);
  return mutationOptions({
    // Behind a removal committed just before it; an Undo waits for its edit.
    scope: { id: "entry-writes" },
    mutationKey: keys.entries,
    meta: { action: "entry edit" },
    mutationFn: ({ id, patch }: { id: string; patch: EntryPatch }) => send(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: keys.entries });
      const live = qc.getQueryData<Entry[]>(keys.entries) ?? [];
      const paged = qc
        .getQueriesData<InfiniteData<EntryPage<Entry>>>({ queryKey: keys.entryPages })
        .flatMap(([, data]) => data?.pages.flatMap((page) => page.entries) ?? []);
      const before = live.find((e) => e.id === id) ?? paged.find((e) => e.id === id);
      if (!before) return { before, inserted: false };
      const flight = editsInFlight.get(id);
      if (flight) flight.count += 1;
      else editsInFlight.set(id, { count: 1, confirmed: before });
      const edit = (e: Entry) => (e.id === id ? withPatch(e, patch) : e);
      // An older row moved into the live window would hide until the refresh:
      // the timeline shows paged rows only from before that window.
      const inserted =
        !live.some((e) => e.id === id) &&
        withPatch(before, patch).at.getTime() >= Date.now() - LIVE_WINDOW_MS;
      qc.setQueryData<Entry[]>(keys.entries, (old = []) =>
        inserted ? [withPatch(before, patch), ...old] : old.map(edit),
      );
      // A row moved to the other baby stays in this baby's pages with its new
      // baby (the log shows a page's rows only for its own baby), so a failed
      // move can put it back; the refresh adds it to the other baby's pages.
      mapPages(qc, (entries) => entries.map(edit));
      return { before, inserted };
    },
    onSuccess: (saved, { patch }) => {
      const flight = editsInFlight.get(saved.id);
      if (flight) flight.confirmed = saved;
      // A newer edit to this row is queued (an Undo): its values stay on
      // screen, and the last reply or the refresh brings the server's row.
      if (flight && flight.count > 1) return;
      // Only the edited fields come from the reply, so a write queued behind
      // this one (a Woke up tap on the nap) keeps its own value on screen.
      const fields = Object.fromEntries(
        Object.keys(patch).map((k) => [k, saved[k as keyof EntryPatch]]),
      ) as Partial<Entry>;
      const merge = (entries: Entry[]) =>
        entries.map((e) =>
          e.id === saved.id ? { ...e, ...fields, updatedAt: saved.updatedAt } : e,
        );
      qc.setQueryData<Entry[]>(keys.entries, (old = []) => merge(old));
      mapPages(qc, merge);
    },
    // Put back each field that still shows this edit: a newer write or refresh wins.
    onError: (_err, { id, patch }, ctx) => {
      if (!ctx?.before) return;
      const confirmed = editsInFlight.get(id)?.confirmed ?? ctx.before;
      const back = (e: Entry) => (e.id === id ? withoutPatch(e, patch, confirmed) : e);
      const shows = (e: Entry) => e.id === id && holdsPatch(e, patch);
      qc.setQueryData<Entry[]>(keys.entries, (rows = []) =>
        ctx.inserted ? rows.filter((e) => !shows(e)) : rows.map(back),
      );
      mapPages(qc, (entries) => entries.map(back));
    },
    onSettled: (_saved, _err, { id }, ctx) => {
      const flight = editsInFlight.get(id);
      if (ctx?.before && flight && --flight.count === 0) editsInFlight.delete(id);
      return qc.invalidateQueries({ queryKey: keys.entries });
    },
  });
}

const fetchChangesRaw = (signal: AbortSignal) =>
  unwrap(
    api.entries.changes.get({
      query: { since: new Date(Date.now() - CHANGE_WINDOW_MS).toISOString() },
      fetch: { signal },
    }),
  );

type RawSnapshot = Awaited<ReturnType<typeof fetchChangesRaw>>[number]["before"];

/** A change's entry, with its ISO times as `Date`s. */
function hydrateSnapshot(raw: RawSnapshot): EntryLike {
  return { ...raw, at: toDate(raw.at), endedAt: raw.endedAt ? toDate(raw.endedAt) : null };
}

/** An assistant's edits and deletes from the last week that can still be put back (#72). */
export function useEntryChanges() {
  const pendingWrites = useIsMutating({ mutationKey: keys.entries });
  return useQuery({
    queryKey: keys.entryChanges,
    queryFn: async ({ signal }): Promise<EntryChange[]> =>
      (await fetchChangesRaw(signal)).map((c) => ({
        ...c,
        at: toDate(c.at),
        before: hydrateSnapshot(c.before),
        after: c.after ? hydrateSnapshot(c.after) : null,
      })),
    ...sharedSyncOptions(pendingWrites),
  });
}

/** Put an assistant's change back: the values from before an edit, or the removed entry. */
export function useRestoreChange() {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: "entry-writes" },
    mutationKey: keys.entries,
    meta: { action: "change restore" },
    mutationFn: (id: string) => unwrap(api.entries.changes({ id }).restore.post()),
    // The entry and the change list both move; both are under keys.entries.
    onSettled: () => qc.invalidateQueries({ queryKey: keys.entries }),
  });
}

// --- Household -------------------------------------------------------------

export type Household = Awaited<ReturnType<typeof fetchHousehold>>;
const fetchHousehold = ({ signal }: { signal: AbortSignal }) =>
  unwrap(api.household.get({ fetch: { signal } }));

export function useHousehold() {
  const pendingWrites = useIsMutating({ mutationKey: keys.household });
  return useQuery({
    queryKey: keys.household,
    queryFn: fetchHousehold,
    ...sharedSyncOptions(pendingWrites),
  });
}

const NOTHING_UNTRACKED: readonly TrackableType[] = [];

/**
 * Which optional types the household tracks (sleep, diaper changes). Until the
 * household loads, or if it can't, everything counts as on: that is what most
 * families use, so their tiles never flash away. `ready` says the answer is
 * the household's own; the checks, milestones, and report wait for it, so
 * they never show a type the family turned off.
 */
export function useTracking(): Tracked & { untracked: readonly TrackableType[]; ready: boolean } {
  const household = useHousehold();
  const untracked = household.data?.untrackedTypes ?? NOTHING_UNTRACKED;
  return {
    ...tracked(untracked),
    untracked,
    ready: household.data !== undefined,
  };
}

/**
 * The unit the household logs bottles in, for showing amounts. Ounces until
 * the household loads, or if it can't: most families' choice, so their
 * numbers never flash from one unit to the other. A write must not guess: the
 * log keeps Log a bottle disabled until the household has loaded.
 */
export function useBottleUnit(): BottleUnit {
  return useHousehold().data?.bottleUnit ?? "oz";
}

/** The unit the household reads weights in; pounds until the household loads. */
export function useWeightUnit(): WeightUnit {
  return useHousehold().data?.weightUnit ?? "lb";
}

export type HouseholdPatch = Parameters<typeof api.household.patch>[0];

export function useUpdateHousehold() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: keys.household,
    meta: { action: "household update" },
    mutationFn: (patch: HouseholdPatch) => unwrap(api.household.patch(patch)),
    onMutate: () => qc.cancelQueries({ queryKey: keys.household }),
    onSuccess: (view) => qc.setQueryData(keys.household, view),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.household }),
  });
}

/** Whether this phone's language is set for the US (also when it can't tell). */
function phoneInUS(): boolean {
  try {
    return new Intl.Locale(navigator.language).maximize().region === "US";
  } catch {
    return true;
  }
}

/**
 * The unit bottles are measured in where this phone is: ounces in the US,
 * millilitres everywhere else. Setup offers it as the household's default.
 */
export function phoneBottleUnit(): BottleUnit {
  return phoneInUS() ? "oz" : "ml";
}

/**
 * The unit a clinic weighs babies in where this phone is: pounds and ounces in
 * the US, kilograms everywhere else. Setup saves it as the household's unit.
 */
export function phoneWeightUnit(): WeightUnit {
  return phoneInUS() ? "lb" : "kg";
}

/** This phone's IANA zone, e.g. America/Chicago. */
export function phoneTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * The app shows phone-local times, but the AI assistant answers from the
 * server, so the household needs a zone on record. The first phone to open the
 * log with none set reports its own; Settings can change it.
 */
export function useReportTimeZone() {
  const household = useHousehold();
  const update = useUpdateHousehold();
  const sending = useRef(false);
  const missing = household.data !== undefined && household.data.timeZone === null;
  const { mutate } = update;
  // Re-checked on every poll, so a failed report is retried on the next one.
  const polled = household.dataUpdatedAt;
  useEffect(() => {
    const zone = phoneTimeZone();
    if (!missing || !zone || sending.current) return;
    sending.current = true;
    mutate({ defaultTimeZone: zone }, { onSettled: () => (sending.current = false) });
  }, [missing, mutate, polled]);
}

export function useCreateInvite() {
  return useMutation({
    meta: { action: "invite create" },
    mutationFn: () => unwrap(api.household.invites.post()),
  });
}

export function useJoinHousehold() {
  return useMutation({
    meta: { action: "household join" },
    mutationFn: (code: string) => unwrap(api.household.join.post({ code })),
  });
}

// --- Billing -------------------------------------------------------------------

/** Opens Polar's checkout for the household's one-time purchase. */
export function useCheckout() {
  return useMutation({
    meta: { action: "checkout" },
    mutationFn: (placement: "settings" | "log") => unwrap(api.billing.checkout.post({ placement })),
    // Polar's page takes over; the household query sees the purchase on return.
    onSuccess: ({ url }) => window.location.assign(url),
  });
}

// --- Connected assistants ---------------------------------------------------

const fetchAssistants = async ({ signal }: { signal: AbortSignal }) =>
  (await unwrap(api.assistants.get({ fetch: { signal } }))).map((a) => ({
    ...a,
    connectedAt: a.connectedAt ? toDate(a.connectedAt) : null,
  }));
export type ConnectedAssistant = Awaited<ReturnType<typeof fetchAssistants>>[number];

/**
 * The assistants this parent allowed (not their partner's). A parent connects
 * one in another app, so the list refreshes on coming back to this one.
 */
export function useAssistants() {
  return useQuery({
    queryKey: keys.assistants,
    queryFn: fetchAssistants,
    refetchOnWindowFocus: "always",
  });
}

export function useRemoveAssistant() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: keys.assistants,
    meta: { action: "assistant remove" },
    mutationFn: (id: string) => unwrap(api.assistants({ id }).delete()),
    onSuccess: (_removed, id) =>
      qc.setQueryData<ConnectedAssistant[]>(keys.assistants, (old = []) =>
        old.filter((a) => a.id !== id),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.assistants }),
  });
}

// --- Bottle alerts (Web Push) ---------------------------------------------------

const fetchPushConfig = ({ signal }: { signal?: AbortSignal }) =>
  unwrap(api.push.get({ fetch: { signal } }));

/**
 * Whether this server sends bottle alerts, and the key browsers subscribe
 * with. Fetched again when Settings opens and on return to the app, so a tab
 * open across a new key shows its device as off. Turning on fetches the key
 * again itself (useTurnOnPush).
 */
export function usePushConfig() {
  return useQuery({
    queryKey: keys.push,
    queryFn: fetchPushConfig,
    refetchOnWindowFocus: true,
  });
}

/** Tell the server this browser's alerts are off. The endpoint goes in the body. */
const forgetEndpoint = (endpoint: string) =>
  unwrap(api.push.subscription.remove.post({ endpoint }));

/**
 * This device's alerts: the browser's subscription as the server knows it,
 * for this parent and household, and the notification permission. Rechecked
 * on return to the app, since the permission changes in browser settings.
 * A subscription made with an older key gets no alerts, so it counts as off.
 * `publicKey` is null while alerts can't be on here (no key, or no Web Push).
 */
export function usePushDevice(publicKey: string | null) {
  return useQuery({
    queryKey: [...keys.pushDevice, publicKey],
    enabled: publicKey !== null,
    refetchOnWindowFocus: "always",
    queryFn: async ({ signal }) => {
      const permission = notificationPermission();
      const subscription = await deviceSubscription();
      if (!subscription || !hasKey(subscription, publicKey!))
        return { subscribed: false, permission };
      const { subscribed } = await unwrap(
        api.push.subscription.status.post(
          { endpoint: subscription.endpoint },
          { fetch: { signal } },
        ),
      );
      return { subscribed, permission };
    },
  });
}

/** Turn this device's alerts on. Not optimistic: the switch moves when the server has it. */
export function useTurnOnPush() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: keys.pushDevice,
    meta: { action: "push on" },
    mutationFn: async (args: { permission: Promise<NotificationPermission> }) => {
      // The server's key now, not the one Settings cached before a key change.
      const currentKey = async () => {
        const config = await qc.fetchQuery({
          queryKey: keys.push,
          queryFn: fetchPushConfig,
          staleTime: 0,
        });
        if (!config.publicKey) throw new Error("This server sends no bottle alerts.");
        return config.publicKey;
      };
      return unwrap(
        api.push.subscription.post(await turnOnPush(args.permission, currentKey, forgetEndpoint)),
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.pushDevice }),
  });
}

export function useTurnOffPush() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: keys.pushDevice,
    meta: { action: "push off" },
    mutationFn: () => turnOffPush(forgetEndpoint),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.pushDevice }),
  });
}

/**
 * Check this device's alerts again. The check sends the browser's analytics
 * choice, so Settings calls this when that choice changes.
 */
export function useRecheckPushDevice() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: keys.pushDevice });
}

/**
 * Before signing out: this device stops getting the household's alerts.
 * Never throws and never waits more than a few seconds, so signing out still
 * works offline; if the server didn't hear, the next alert finds it gone.
 */
export async function forgetPushDevice(): Promise<void> {
  const forget = turnOffPush(forgetEndpoint);
  await Promise.race([forget.catch(() => undefined), new Promise((r) => setTimeout(r, 3_000))]);
}

// --- Feedback ----------------------------------------------------------------

export type FeedbackKind = NonNullable<Parameters<typeof api.feedback.post>[0]["kind"]>;

/**
 * A message to Babia's makers, sent with the screen it came from. Not
 * optimistic: the sheet keeps the words until the server has them.
 */
export function useSendFeedback() {
  return useMutation({
    meta: { action: "feedback send" },
    mutationFn: (body: { kind: FeedbackKind; message: string }) =>
      unwrap(api.feedback.post({ ...body, page: window.location.pathname })),
  });
}

// --- Health ----------------------------------------------------------------

export function useHealth() {
  return useQuery({
    queryKey: keys.health,
    queryFn: () => unwrap(api.health.get()),
    refetchInterval: 30_000,
  });
}
